/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import get from 'lodash/get';
import isNil from 'lodash/isNil';

import SetupErrorType from './setup-error-type';

/**
 * Pomocná třida pro registraci zařízení na vzdáleném serveru.
 */
class SetupManager {
  /**
   * Vytvoří instanci.
   * @param {OrganizationHelper} organizationHelper Pomocník pro zpracování struktury organizací.
   * @param {SetupErrorFactory} errorFactory Továrna na chybové objekty.
   * @returns {SetupManager} Nová instance.
   */
  constructor(organizationHelper, errorFactory) {
    this.errorFactory = errorFactory;
    this.organizationHelper = organizationHelper;
  }

  /**
   * Vytvoří chybový objekt pro selhání při registraci.
   * @param {number} status HTTP status kód chybné registrace.
   * @param {string} text Tělo odpovědi chybné registrace jako prostý text.
   * @private
   * @returns {Error}
   */
  createError(status, text) {
    const errorType = this.parseErrorType(text);
    if (isNil(errorType)) {
      return new Error(`HTTP status ${status}.`);
    }
    return this.errorFactory.create(errorType);
  }

  /**
   * Zjistí seznam organizací na serveru, do kterých lze zařízení přiřadit.
   * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
   * @param {string} username Jméno uživatele na serveru.
   * @param {string} password Heslo uživatele na serveru.
   * @returns {Promise.<Array.<Organization>>} Seznam organizací.
   * @throws {SetupError} Při registraci se vyskytla chyba.
   */
  fetchOrganizations(serverUrl, username, password) {
    return new Promise((resolve) =>
      resolve(
        // Odešle se požadavek s přihlašovacími údaji na server.
        fetch(`${serverUrl}/api/register/verify`, {
          body: JSON.stringify({ username, password }),
          headers: { 'Content-type': 'application/json' },
          method: 'POST'
        }).then((response) => {
          // Pokud je odpověď v pořádku, pokusíme se ji zpracovat jako JSON.
          if (response.ok) {
            return response
              .json()
              .then((json) => this.parseOrganizations(json));
          }
          // Jakákoliv jiná odpověď serveru se považuje za chybu.
          // Vytvoří se specifická chyba s dodatečnými informacemi, které server nahlásil.
          return response.text().then((text) => {
            throw this.createError(response.status, text);
          });
        })
      )
    );
  }

  /**
   * Zjistí URL adresu pro registraci zařízení na serveru pomocí QR kódu.
   * Adresa se má otevřít v prohlížeči přes které se provede vytvoření zařízení.
   * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
   * @param {string} fingerprint Jedinečný otisk zařízení, který se má zaregistrovat.
   * @returns {string} URL pro manuální registraci.
   */
  getQrUrl(serverUrl, fingerprint) {
    return `${serverUrl}/api/register/url/lighter/${fingerprint}`;
  }

  /**
   * Zjistí typ chyby registrace.
   * @param {string} text Tělo odpovědi chybné registrace jako prostý text.
   * @private
   * @returns {SetupErrorType}
   */
  parseErrorType(text) {
    switch (text) {
      case 'device.alreadyExists':
        return SetupErrorType.DeviceExists;
      case 'device.alreadyRegistered':
        return SetupErrorType.DeviceRegistered;
      case 'device.missingAccessCode':
        return SetupErrorType.AccessCodeMissing;
      case 'device.saveError':
        return SetupErrorType.SaveError;
      case 'organization.deviceLimitExceeded':
        return SetupErrorType.DeviceLimitExceeded;
      case 'organization.invalid':
        return SetupErrorType.OrganizationInvalid;
      case 'organization.missingOrganization':
        return SetupErrorType.OrganizationMissing;
      case 'request.invalid':
        return SetupErrorType.RequestInvalid;
      case 'user.invalid':
        return SetupErrorType.UserInvalid;
      default:
      // Neznámý typ chyby
    }
  }

  /**
   * Zjistí identifikátor zařízení z úspěšné registrace.
   * @param {Object} json Tělo odpovědi úspěšné registrace jako JSON objekt.
   * @private
   * @returns {string}
   */
  parseDeviceId(json) {
    return json.device.deviceId;
  }

  /**
   * Zjistí seznam organizací z úspěšné odpovědi.
   * @param {Object} json Tělo odpovědi úspěšně stažených organizací jako JSON objekt.
   * @private
   * @returns {Array.<Organization>}
   */
  parseOrganizations(json) {
    // Kořenová organizace vrácené struktury.
    // Pokud neexistuje, vrátíme prázdný seznam.
    const root = get(json, 'organization');
    if (isNil(root)) {
      return [];
    }
    // Ze stromové struktury organizací uděláme plochý seznam.
    return this.organizationHelper.flatten(root);
  }

  /**
   * Automaticky zaregistruje zařízení na vzdáleném serveru.
   * Stačí znát otisk ke kterému se nalezne vytvořené zařízení na serveru.
   * Zařízení se na serveru vytvoří např. pomocí odkazu z QR kódu.
   * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
   * @param {string} fingerprint Jedinečný otisk zařízení.
   * @returns {Promise.<string|null>} Identifikátor, který je danému zařízení
   * přiřazen na serveru, nebo `null` pokud zařízení není registrované.
   * @throws {SetupError} Při registraci se vyskytla chyba.
   */
  registerAutomatic(serverUrl, fingerprint) {
    return new Promise((resolve) =>
      resolve(
        // Odešleme na server požadavek s otiskem zařízení.
        fetch(`${serverUrl}/api/register/sync`, {
          body: JSON.stringify({ accessCode: fingerprint }),
          headers: { 'Content-type': 'application/json' },
          method: 'POST'
        }).then((response) => {
          if (response.ok) {
            // Pokud je odpověď v pořádku, pokusíme se ji zpracovat jako JSON.
            return response.json().then((json) => this.parseDeviceId(json));
          }
          // Status 404 značí, že nebylo nalezeno zařízení s předaným otiskem.
          // Vrací se tedy null.
          const { status } = response;
          if (response.status === 404) {
            return null;
          }
          // Jakákoliv jiná odpověď serveru se považuje za chybu.
          throw new Error(`HTTP status ${status}.`);
        })
      )
    );
  }

  /**
   * Manuálně zaregistruje zařízení na vzdáleném serveru.
   * K tomu je potřeba znát uživatele a organizaci do které se má zařízení vytvořit.
   * @param {string} serverUrl Adresa serveru, na kterém se zařízení registruje.
   * @param {string} fingerprint Jedinečný otisk zařízení.
   * @param {string} username Jméno uživatele na serveru.
   * @param {string} password Heslo uživatele na serveru.
   * @param {string} deviceName Název zařízení na serveru.
   * @param {number} organizationId Identifikátor organizace, do které se má zařízení přiřadit.
   * @returns {Promise.<string>} Identifikátor právě registrovaného zařízení.
   * @throws {SetupError} Při registraci se vyskytla chyba.
   */
  registerManual(
    serverUrl,
    fingerprint,
    username,
    password,
    deviceName,
    organizationId
  ) {
    return new Promise((resolve) =>
      resolve(
        fetch(`${serverUrl}/api/register/device`, {
          body: JSON.stringify({
            accessCode: fingerprint,
            deviceName,
            deviceType: 'lighter',
            organizationId,
            password,
            username
          }),
          headers: { 'Content-type': 'application/json' },
          method: 'POST'
        }).then((response) => {
          if (response.ok) {
            // Pokud je odpověď v pořádku, pokusíme se ji zpracovat jako JSON.
            return response.json().then((json) => this.parseDeviceId(json));
          }
          // Jakákoliv jiná odpověď serveru se považuje za chybu.
          // Vytvoří se specifická chyba s dodatečnými informacemi, které server nahlásil.
          return response.text().then((text) => {
            throw this.createError(response.status, text);
          });
        })
      )
    );
  }
}

export default SetupManager;
